import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import MovieCard from "@/components/feature/MovieCard";
import SubCategoryTabs from "@/pages/category/components/SubCategoryTabs";
import FilterBar, { type Filters } from "@/pages/category/components/FilterBar";

const PAGE_SIZE = 8;

const defaultFilters: Filters = { type: "全部", region: "全部", year: "全部", sort: "latest" };

const titleMap: Record<string, string> = {
  全部: "全部影视",
  电影: "激情电影",
  电视剧: "激情电视剧",
  网络剧: "网络剧集",
  短片: "精选短片",
};

export default function Category() {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get("type") || "全部";

  const [filters, setFilters] = useState<Filters>({ ...defaultFilters, type: initialType });
  const [page, setPage] = useState(1);
  const [loadMore, setLoadMore] = useState(0);

  const categoryName = titleMap[filters.type] || "全部影视";

  useEffect(() => {
    document.title = `激情视频 - ${categoryName}精选激情影视作品`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        `探索激情视频中丰富的${categoryName}影视作品，包括最新上映、经典推荐，满足您的视觉激情需求。`,
      );
    }
  }, [categoryName]);

  const filtered = useMemo(() => {
    const list = movies.filter((m) => {
      const typeOk = filters.type === "全部" || m.type === filters.type;
      const regionOk = filters.region === "全部" || m.region === filters.region;
      const yearOk = filters.year === "全部" || String(m.year) === filters.year;
      return typeOk && regionOk && yearOk;
    });
    if (filters.sort === "popular") list.sort((a, b) => b.popularity - a.popularity);
    else if (filters.sort === "rating") list.sort((a, b) => b.rating - a.rating);
    else list.sort((a, b) => b.year - a.year);
    return list;
  }, [filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const displayed = filtered.slice(start, start + PAGE_SIZE + loadMore);
  const hasMore = start + PAGE_SIZE + loadMore < filtered.length;

  const updateFilter = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setLoadMore(0);
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setPage(1);
    setLoadMore(0);
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "影视分类", to: "/category" }, { label: categoryName }]} />

      <header className="mt-5 rounded-lg bg-primary-950 p-6 md:p-8">
        <span className="font-display text-xs tracking-[0.3em] text-primary-300">CATEGORY</span>
        <h1 className="mt-2 text-2xl font-black text-background-50 md:text-3xl">{categoryName}</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
          {categoryName}聚合了当下备受关注的激情题材作品，涵盖最新上映、经典口碑与黑马推荐，支持按地区、年份与评分自由筛选，帮你快速锁定想看的那一部。
        </p>
      </header>

      <div className="mt-6">
        <SubCategoryTabs value={filters.type} onChange={(v) => updateFilter("type", v)} />
      </div>

      <div className="mt-5">
        <FilterBar
          filters={filters}
          onChange={updateFilter}
          onReset={resetFilters}
          resultCount={filtered.length}
        />
      </div>

      <div className="mt-6">
        {displayed.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {displayed.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <EmptyState onAction={resetFilters} actionLabel="重置筛选条件" />
        )}
      </div>

      {displayed.length > 0 && (
        <div className="mt-10 flex flex-col items-center gap-6">
          {hasMore && (
            <button
              type="button"
              onClick={() => setLoadMore((n) => n + PAGE_SIZE)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-6 py-3 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-100 cursor-pointer"
            >
              <i className="ri-add-line text-base" />
              加载更多
            </button>
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={(p) => {
              setPage(p);
              setLoadMore(0);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </div>
      )}
    </div>
  );
}